import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, TrendingUp, AlertTriangle, BarChart3, User } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const ACTION_LABELS_SHORT = {
  create_work_entry: "Heures travaillées",
  create_absence: "Absence",
  create_client: "Nouveau client",
  update_client: "Modif client",
  create_order: "Nouvelle commande",
  update_order: "Modif commande",
  register_payment: "Paiement",
  refund: "Remboursement",
  create_sav: "SAV",
  update_sav: "Modif SAV",
  close_sav: "Clôture SAV",
  create_admin_task: "Tâche admin",
  close_admin_task: "Tâche terminée",
  update_material_price: "Prix matière",
  create_bonus: "Prime",
  edit_employee: "Modif salarié",
  disable_employee: "Sortie salarié",
  create_equipment: "Nouvel équipement",
  edit_equipment: "Modif équipement",
  retire_equipment: "Équipement retiré",
  create_life_event: "Événement marquant",
  // Phase 80
  create_training: "Formation",
  create_pv: "PV",
  adjust_cp_rtt: "CP / RTT",
  create_subcontractor: "Nouv. sous-traitant",
  assign_subcontractor_to_order: "Attribution ST",
  create_sub_life_event: "Évt. sous-traitant",
  create_supplier: "Nouv. fournisseur",
  create_supplier_product: "Produit fournisseur",
  lock_order_material: "Matière verrouillée",
  send_deposit_reminder: "Relance acompte",
  toggle_order_paid: "Marquer payée",
  hide_order: "Masquer cmd.",
  put_order_on_hold: "Mise en attente",
  create_seller: "Nouv. vendeur",
  add_vehicle_maintenance: "Entretien véhicule",
  update_vehicle_inspection: "CT / antipoll.",
  create_backlog_task: "Tâche backlog",
  assign_admin_task: "Assignation tâche",
  // Phase 81
  mark_order_completed: "Clôture cmd.",
  cancel_order: "Annulation cmd.",
  update_order_planning_constraints: "Contraintes planning",
  create_supplier_price: "Nouveau prix",
  add_stock_entry: "Mouvement stock",
  edit_supplier: "Modif fournisseur",
  edit_supplier_product: "Modif produit",
  create_salary_entry: "Rémunération",
  edit_training: "Modif formation",
  edit_subcontractor: "Modif sous-traitant",
  disable_subcontractor: "Désact. ST",
  create_annual_objective: "Objectif annuel",
  create_travel_bonus_rate: "Grille déplacement",
  complete_admin_occurrence: "Coche échéance",
  create_recurring_task: "Tâche récurrente",
  create_order_type: "Nouveau type cmd.",
  create_client_profile_from_order: "Fiche CRM",
  create_contract: "Nouveau contrat",
  none: "Non actionnable",
};

// Phase 81: family mapping to filter/group actions
const ACTION_FAMILY = {
  create_work_entry: "rh",
  create_absence: "rh",
  create_bonus: "rh",
  edit_employee: "rh",
  disable_employee: "rh",
  create_life_event: "rh",
  create_training: "rh",
  create_pv: "rh",
  adjust_cp_rtt: "rh",
  create_client: "clients",
  update_client: "clients",
  create_order: "commandes",
  update_order: "commandes",
  register_payment: "commandes",
  refund: "commandes",
  hide_order: "commandes",
  put_order_on_hold: "commandes",
  toggle_order_paid: "commandes",
  send_deposit_reminder: "commandes",
  create_sav: "sav",
  update_sav: "sav",
  close_sav: "sav",
  create_subcontractor: "sous_traitants",
  assign_subcontractor_to_order: "sous_traitants",
  create_sub_life_event: "sous_traitants",
  create_supplier: "fournisseurs",
  create_supplier_product: "fournisseurs",
  update_material_price: "fournisseurs",
  lock_order_material: "fournisseurs",
  create_equipment: "materiel",
  edit_equipment: "materiel",
  retire_equipment: "materiel",
  add_vehicle_maintenance: "materiel",
  update_vehicle_inspection: "materiel",
  create_admin_task: "administratif",
  close_admin_task: "administratif",
  create_backlog_task: "administratif",
  assign_admin_task: "administratif",
  create_seller: "vendeurs",
  // Phase 81
  mark_order_completed: "commandes",
  cancel_order: "commandes",
  update_order_planning_constraints: "commandes",
  create_client_profile_from_order: "clients",
  create_supplier_price: "fournisseurs",
  add_stock_entry: "fournisseurs",
  edit_supplier: "fournisseurs",
  edit_supplier_product: "fournisseurs",
  create_salary_entry: "rh",
  edit_training: "rh",
  edit_subcontractor: "sous_traitants",
  disable_subcontractor: "sous_traitants",
  create_annual_objective: "administratif",
  create_travel_bonus_rate: "rh",
  complete_admin_occurrence: "administratif",
  create_recurring_task: "administratif",
  create_order_type: "administratif",
  create_contract: "administratif",
  none: "autres",
};

const FAMILY_LABEL = {
  all: "Toutes les familles",
  rh: "👷 RH",
  clients: "🧑 Clients",
  commandes: "📋 Commandes",
  sav: "🛠 SAV",
  sous_traitants: "🤝 Sous-traitants",
  fournisseurs: "📦 Fournisseurs",
  materiel: "🔧 Matériel",
  administratif: "🗓 Administratif",
  vendeurs: "💼 Vendeurs",
  autres: "• Autres",
};

const StatCard = ({ label, value, sub, Icon, color = "primary" }) => (
  <Card><CardContent className="pt-5 pb-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-heading font-bold mt-1 font-mono text-${color}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <Icon className={`h-6 w-6 text-${color}`} strokeWidth={1.5} />
    </div>
  </CardContent></Card>
);

export const AIActivityTab = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [family, setFamily] = useState("all");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.aiStats(days)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading && !stats) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!stats) {
    return <p className="text-sm text-muted-foreground text-center py-6">Aucune donnée.</p>;
  }

  const chartData = stats.per_day.map((d) => ({ ...d, label: d.date.slice(5) }));

  // Phase 81: per-family aggregation from per_type
  const perFamily = {};
  (stats.per_type || []).forEach((t) => {
    const fam = ACTION_FAMILY[t.action_type] || "autres";
    const g = (perFamily[fam] = perFamily[fam] || { family: fam, total: 0, executed: 0, types: 0 });
    g.total += t.total;
    g.executed += t.executed;
    g.types += 1;
  });
  const perFamilyList = Object.values(perFamily).sort((a, b) => b.total - a.total);

  const perTypeFiltered = family === "all"
    ? stats.per_type
    : (stats.per_type || []).filter((t) => (ACTION_FAMILY[t.action_type] || "autres") === family);

  return (
    <div className="space-y-6" data-testid="ai-activity-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Activité IA
          </h2>
          <p className="text-sm text-muted-foreground">
            Depuis le {stats.from_date} — adoption, taux d'exécution et types d'actions utilisés.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger className="w-[200px]" data-testid="ai-stats-family">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FAMILY_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[140px]" data-testid="ai-stats-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 jours</SelectItem>
              <SelectItem value="30">30 jours</SelectItem>
              <SelectItem value="90">90 jours</SelectItem>
              <SelectItem value="365">1 an</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Instructions" value={stats.totals.total} Icon={BarChart3} />
        <StatCard label="Exécutées" value={stats.totals.executed} sub={`Taux ${stats.totals.execution_rate}%`} Icon={TrendingUp} color="emerald-700" />
        <StatCard label="Annulées" value={stats.totals.cancelled} Icon={AlertTriangle} color="slate-600" />
        <StatCard label="Confiance basse" value={`${stats.low_confidence_rate}%`} sub="confidence < 45%" Icon={AlertTriangle} color="amber-700" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Répartition par famille</CardTitle>
        </CardHeader>
        <CardContent>
          {perFamilyList.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">—</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="ai-stats-families">
              {perFamilyList.map((f) => {
                const active = family === f.family;
                const rate = f.total > 0 ? Math.round((f.executed / f.total) * 100) : 0;
                return (
                  <button
                    key={f.family}
                    type="button"
                    onClick={() => setFamily(active ? "all" : f.family)}
                    className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                      active ? "border-primary bg-primary/10" : "hover:border-primary/40 hover:bg-muted/40"
                    }`}
                    data-testid={`ai-family-${f.family}`}
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <span>{FAMILY_LABEL[f.family] || f.family}</span>
                      <Badge variant="outline" className="font-mono text-xs">{f.total}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>{f.types} type{f.types > 1 ? "s" : ""}</span>
                      <span className="text-emerald-700">{rate}% exécutées</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Activité quotidienne</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.per_day.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Aucune activité.</p>
          ) : (
            <div className="h-48 min-w-[200px]" data-testid="ai-stats-daily-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={192}>
                <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <defs>
                    <linearGradient id="aiDailyFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={28} />
                  <Tooltip
                    labelFormatter={(l, payload) => payload?.[0]?.payload?.date || l}
                    formatter={(v) => [`${v} instruction(s)`, "Total"]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#aiDailyFill)" dot={{ r: 2 }} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Types d'actions les plus utilisés
              {family !== "all" && (
                <Badge variant="outline" className="ml-2 text-xs">{FAMILY_LABEL[family]}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perTypeFiltered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {family === "all" ? "—" : "Aucune action IA dans cette famille"}
              </p>
            ) : (
              <ul className="space-y-2" data-testid="ai-stats-per-type">
                {perTypeFiltered.slice(0, 10).map((t) => {
                  const rate = t.total > 0 ? Math.round((t.executed / t.total) * 100) : 0;
                  return (
                    <li key={t.action_type} className="flex items-center gap-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span>{ACTION_LABELS_SHORT[t.action_type] || t.action_type}</span>
                          <span className="font-mono text-xs">{t.total}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded mt-1 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500"
                            style={{ width: `${rate}%` }}
                            title={`Exécuté ${rate}%`}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" /> Top utilisateurs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_users.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">—</p>
            ) : (
              <ul className="space-y-2" data-testid="ai-stats-top-users">
                {stats.top_users.map((u, i) => {
                  const rate = u.total > 0 ? Math.round((u.executed / u.total) * 100) : 0;
                  return (
                    <li key={u.user_id} className="flex items-center gap-3 text-sm">
                      <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold grid place-items-center">
                        #{i + 1}
                      </span>
                      <span className="flex-1 truncate font-medium">{u.name}</span>
                      <Badge variant="outline" className="text-xs">{u.total} actions</Badge>
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">{rate}% exécutées</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIActivityTab;
